/**
 * @fileoverview Public Microsoft 365 IT handoff page for secure configuration submission.
 * @module system-web/pages/SsoItHandoffPage
 *
 * Responsibilities:
 * - Present the one-time setup packet and collect verified, confirmed Entra settings.
 * - Distinguish invalid, expired, and consumed handoff links.
 * Dependencies:
 * - useSsoHandoff hook and DefensibleLogo presentation component.
 * Architecture:
 * - Inherently online public handoff; token access and submission are service-backed.
 * Notes:
 * - Secret value is cleared after every submit attempt and is never logged or telemetered.
 *
 * @component
 * Purpose: Lets a Department IT contact provide required Microsoft Entra setup values.
 * Props: Route token from the public URL.
 * Behavior: Validates required values and confirmations before one-time submission.
 * Data Source: remote via the public handoff service/hook.
 * Constraints: No staff authentication; never exposes a submitted secret again.
 * Performance: Loads one handoff record and packet per route token.
 *
 * @security
 * Validation: IDs, domain, secret expiry, verified email domains, and four confirmations.
 * Trust Boundary: Public route token is passed only to the handoff hook.
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DefensibleLogo } from '@ui-common';
import { useSsoHandoff } from '../hooks/useSsoHandoff';

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const onMicrosoftDomain = /^[a-z0-9-]+\.onmicrosoft\.com$/i;
const emailDomain = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const checks = [
  { key: 'redirectUrisRegistered', label: 'Both callback URLs are registered as Web redirect URIs (not SPA).' },
  { key: 'graphPermissionsConsented', label: 'Admin consent was granted for openid, profile, email, and User.Read.' },
  { key: 'optionalClaimsConfigured', label: 'The email, given_name, family_name, and upn optional ID claims are configured.' },
  { key: 'userAssignmentRequiredEnabled', label: 'Assignment is required and approved users/groups are assigned.' },
];
const emptyValues = {
  email: '',
  clientId: '',
  clientSecret: '',
  upstreamTenantId: '',
  onMicrosoftDomain: '',
  secretExpiresAt: '',
  verifiedEmailDomains: '',
};

export default function SsoItHandoffPage() {
  const { pathname } = useLocation();
  const encodedToken = pathname.slice('/external/sso-handoff/'.length).split('/')[0] ?? '';
  let token = '';
  try {
    token = decodeURIComponent(encodedToken);
  } catch {
    token = '';
  }
  const { status, loading, submitting, loadError, loadErrorCode, submitError, submit } = useSsoHandoff(token);
  const [values, setValues] = useState(emptyValues);
  const [confirmations, setConfirmations] = useState<Record<string, boolean>>({});
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [secretError, setSecretError] = useState('');

  const expired = loadErrorCode === 'HANDOFF_EXPIRED' || status?.expired;
  const consumed = loadErrorCode === 'HANDOFF_CONSUMED' || status?.consumed;
  const invalid = Boolean(loadError) && !expired && !consumed;

  const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    setSecretError('');
    if (uuid.test(values.clientSecret.trim())) {
      setSecretError('You entered the Client Secret ID (a GUID), not the Client Secret Value. Copy the Value column from Microsoft Entra → Certificates & secrets.');
      return;
    }
    const domains = values.verifiedEmailDomains.split(',').map((value) => value.trim()).filter(Boolean);
    if (!uuid.test(values.clientId.trim()) || !uuid.test(values.upstreamTenantId.trim())) {
      setFormError('Enter a valid Application (client) ID and Directory (tenant) ID.');
      return;
    }
    if (!onMicrosoftDomain.test(values.onMicrosoftDomain.trim())) {
      setFormError('Enter your primary .onmicrosoft.com domain. A custom email domain is not a substitute.');
      return;
    }
    if (!emailDomain.test(values.email.trim().split('@')[1] ?? '')) {
      setFormError('Enter the Department IT contact email address to confirm the handoff recipient.');
      return;
    }
    if (!values.clientSecret.trim()) {
      setFormError('Enter the Client Secret Value.');
      return;
    }
    if (!values.secretExpiresAt || new Date(`${values.secretExpiresAt}T23:59:59`).getTime() < Date.now() + 7 * 86400000) {
      setFormError('The client secret expiration date must be at least seven days in the future.');
      return;
    }
    if (domains.length === 0 || domains.some((domain) => !emailDomain.test(domain))) {
      setFormError('Enter one or more valid verified organizational email domains separated by commas.');
      return;
    }
    if (checks.some(({ key }) => confirmations[key] !== true)) {
      setFormError('Confirm all four configuration requirements before submitting.');
      return;
    }
    const payload = {
      email: values.email.trim(),
      clientId: values.clientId.trim(),
      clientSecret: values.clientSecret,
      upstreamTenantId: values.upstreamTenantId.trim(),
      onMicrosoftDomain: values.onMicrosoftDomain.trim(),
      secretExpiresAt: new Date(`${values.secretExpiresAt}T23:59:59`).toISOString(),
      verifiedEmailDomains: domains,
      itConfirmations: confirmations,
    };
    void submit(payload).then(() => {
      setValues(emptyValues);
      setSubmitted(true);
    }).catch(() => {
      setValues((currentValues) => ({ ...currentValues, clientSecret: '' }));
    });
  };

  if (loading) return <main className="grid min-h-screen place-items-center bg-slate-50 p-6"><section className="text-center"><DefensibleLogo size="md" subtitle="Secure IT handoff" /><p className="mt-8 text-sm text-slate-600">Verifying your one-time secure link…</p></section></main>;
  if (invalid || expired || consumed) {
    const title = consumed
      ? 'This handoff link has already been used'
      : expired
        ? 'This handoff link has expired'
        : 'This handoff link is invalid';
    const message = consumed
      ? 'A one-time handoff link can only be submitted once. Contact your Streamline administrator for a new link if a correction is needed.'
      : expired
        ? 'The seven-day handoff window has ended. Contact your Streamline administrator to request a fresh secure handoff.'
        : loadError ?? 'The handoff link could not be verified.';
    return <main className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <section className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <DefensibleLogo size="lg" subtitle="Secure IT handoff" />
        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">One-time department setup link</p>
        <h1 className="mt-3 text-2xl font-semibold text-slate-900">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">{message}</p>
        <p className="mt-6 rounded-lg bg-slate-50 p-4 text-sm text-slate-700">For security, this link cannot be reused. Contact your Streamline administrator to request a new secure handoff link.</p>
      </section>
    </main>;
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6">
      <div className="mx-auto grid max-w-6xl items-start gap-6 lg:grid-cols-[minmax(240px,340px)_minmax(480px,680px)] lg:justify-center">
        <aside className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-700 shadow-sm">
          <DefensibleLogo size="md" subtitle="Streamline Automation" />
          <h1 className="mt-7 text-xl font-semibold text-slate-900">Secure department setup</h1>
          <p className="mt-2 leading-6">Complete the values requested by your Streamline administrator. This one-time link expires {status?.expiresAt ? new Date(status.expiresAt).toLocaleString() : 'in seven days'}.</p>
          {status?.packet && <div className="mt-5 rounded-lg bg-sky-50 p-3 text-sky-950">
            <span className="block text-xs font-semibold uppercase tracking-wide">Setup packet</span>
            <strong className="mt-1 block">{status.packet.referenceId} · Version {status.packet.version}</strong>
          </div>}
          {status?.html && <details className="mt-4 rounded-lg border border-slate-200 p-3">
            <summary className="cursor-pointer text-sm font-semibold">View the IT setup packet</summary>
            <article className="prose mt-3 max-h-96 overflow-auto border-t pt-3" dangerouslySetInnerHTML={{ __html: status.html }} />
          </details>}
          <div className="mt-5 border-t border-slate-200 pt-4">
            <p className="font-semibold text-slate-900">Before you submit</p>
            <ul className="mt-2 list-inside list-disc space-y-2 text-xs leading-5 text-slate-600">
              <li>Copy the Client Secret Value, not its Secret ID.</li>
              <li>Confirm the redirect URIs use the Web platform.</li>
              <li>Secrets are encrypted and are never shown again.</li>
            </ul>
          </div>
        </aside>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4">
            <div className="flex items-center gap-3"><DefensibleLogo size="sm" /><div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">One-time secure handoff</p><p className="font-semibold text-slate-900">Microsoft Entra configuration</p></div></div>
            <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-red-800">Confidential</span>
          </header>
          {submitted ? <div className="px-6 py-12 text-center">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-green-100 text-xl font-bold text-green-800">✓</span>
            <h2 className="mt-4 text-xl font-semibold text-slate-900">Configuration submitted securely</h2>
            <p className="mt-2 text-sm text-slate-600">Your Streamline administrator can now validate the connection. You may close this window.</p>
          </div> : <form className="space-y-5 p-5 sm:p-7" onSubmit={submitForm}>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Connect {status?.departmentName ?? status?.tenantName ?? 'your department'}</h2>
              <p className="mt-1 text-sm leading-6 text-slate-600">Enter the values from your Entra app registration. Client secrets are encrypted at rest and never displayed again.</p>
            </div>
            <label className="block text-sm font-medium text-slate-800">Department IT contact email (confirm recipient)
              <input required type="email" autoComplete="email" className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm font-medium text-slate-800">Application (client) ID<input required className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.clientId} onChange={(event) => setValues({ ...values, clientId: event.target.value })} /></label>
              <label className="block text-sm font-medium text-slate-800">Directory (tenant) ID<input required className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.upstreamTenantId} onChange={(event) => setValues({ ...values, upstreamTenantId: event.target.value })} /></label>
              <label className="block text-sm font-medium text-slate-800">Primary Microsoft tenant domain<input required placeholder="Primary tenant domain ending in .onmicrosoft.com" className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.onMicrosoftDomain} onChange={(event) => setValues({ ...values, onMicrosoftDomain: event.target.value })} /></label>
              <label className="block text-sm font-medium text-slate-800">Verified organizational email domains<input required placeholder="Enter each verified domain, separated by commas" className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.verifiedEmailDomains} onChange={(event) => setValues({ ...values, verifiedEmailDomains: event.target.value })} /></label>
              <label className="block text-sm font-medium text-slate-800">Client secret Value<input required type="password" autoComplete="new-password" className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.clientSecret} onChange={(event) => setValues({ ...values, clientSecret: event.target.value })} /></label>
              <label className="block text-sm font-medium text-slate-800">Client secret expiration date<input required type="date" className="mt-1 w-full rounded-lg border border-slate-300 p-2.5" value={values.secretExpiresAt} onChange={(event) => setValues({ ...values, secretExpiresAt: event.target.value })} /></label>
            </div>
            <fieldset className="space-y-3 rounded-xl border border-slate-200 p-4">
              <legend className="px-1 text-sm font-semibold text-slate-900">Confirm the Microsoft Entra setup</legend>
              {checks.map(({ key, label }) => <label key={key} className="flex items-start gap-2 text-sm leading-5 text-slate-700"><input type="checkbox" className="mt-1" checked={confirmations[key] ?? false} onChange={(event) => setConfirmations({ ...confirmations, [key]: event.target.checked })} />{label}</label>)}
            </fieldset>
            {secretError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{secretError}</p>}
            {formError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{formError}</p>}
            {submitError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{submitError}</p>}
            <button type="submit" disabled={submitting} className="w-full rounded-lg bg-sky-900 px-4 py-3 font-semibold text-white hover:bg-sky-800 disabled:opacity-50">{submitting ? 'Submitting securely…' : 'Submit credentials securely'}</button>
          </form>}
        </section>
      </div>
    </main>
  );
}
